/*
    Build-completion overlay for the segmented Budget screen.

    The screen renders end to end, which makes it easy to read as finished — most of what is
    on it is real, but some elements are draft-only, some have no field behind them, and two
    render a value that can be wrong. Marking them in the page itself keeps that distinction
    visible during review instead of living in a document nobody has open at the time.

    Off by default. Visible in dev, or anywhere with `?todo` on the URL so a deployed
    preview can be reviewed the same way.
*/

export type TodoKind =
  /** No field behind it. Renders a placeholder, a constant, or nothing. */
  | "data"
  /** Behaves correctly against the draft; discarded on refresh for want of a save path. */
  | "draft"
  /** Renders, but the value it shows can be incorrect. */
  | "wrong"

export const TODO_LABEL: Record<TodoKind, string> = {
  data: "Needs data",
  draft: "Draft only",
  wrong: "Can be wrong",
}

export const TODO_ROOT_CLASS = "seg-todo-on"

/**
 * Spread onto the element that needs the work:
 *
 *   <span {...todo("data", "Hardcoded — needs Launch.spend_pattern")}>
 *
 * Returns nothing when the overlay is unavailable, so production markup is untouched.
 */
export const todo = (kind: TodoKind, note: string) =>
  todoAvailable()
    ? // `title` carries the note on hover. It sits on the wrapper, so a control with its
      // own tooltip inside keeps it.
      { "data-todo": kind, "data-todo-note": note, title: `${TODO_LABEL[kind]} — ${note}` }
    : {}

let available: boolean | null = null

/** Dev build, or any environment with `?todo` on the URL. Resolved once. */
export const todoAvailable = (): boolean => {
  if (available !== null) return available
  let flagged = false
  try {
    flagged = new URLSearchParams(window.location.search).has("todo")
  } catch {
    flagged = false
  }
  available = Boolean(import.meta.env.DEV) || flagged
  return available
}

export const countTodos = (): Record<TodoKind, number> => {
  const out: Record<TodoKind, number> = { data: 0, draft: 0, wrong: 0 }
  if (!todoAvailable()) return out
  document.querySelectorAll<HTMLElement>("[data-todo]").forEach((el) => {
    const kind = el.dataset.todo as TodoKind
    if (kind in out) out[kind] += 1
  })
  return out
}
